// File for logic corresponding to accordion component
import { Component } from 'js-component-framework';
import breakpoints from '@/js/utils/breakpoints';
import './index.scss';
import AriaAccordion from '@/js/utils/AriaAccordion';
import Debounce from '../../js/utils/Debounce';

/**
 * Component for accordion
 */
export default class Accordion extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    const {
      accordionBreakpoint: breakpoint,
      slideToRefs,
    } = this.element.dataset;
    const { controller } = this.children;

    this.accordionDebounce = new Debounce(
      this.onResize.bind(this),
      'resize',
      window,
      250,
      false,
    );

    /*
     * Always initialize accordion without a defined breakpoint
     * OR initialize based on a set breakpoint passed via data attribute.
     *
     * See `breakpoints.js` for breakpoint values.
     */
    if ((typeof breakpoint === 'undefined') || this.isActiveBreakpoint()) {
      this.initAccordion();
    } else if (controller && !this.isActiveBreakpoint()) {
      // Disable the controller to trigger the accordion if breakpoint query does not match
      controller.disabled = true;
    }

    /**
     * Allows user to jump to hashed references that are contained within the
     * accordion drawer.
     */
    if (slideToRefs) {
      this.initArticleFootnotes();
    }
  }

  /**
   * Stuff to do on window resize.
   */
  onResize() {
    if (this.accordion && !this.isActiveBreakpoint()) {
      this.destroy();
    } else if (typeof this.accordion === 'undefined' && this.isActiveBreakpoint()) {
      this.initAccordion();
    }
  }

  /**
   * Destroy accordion instance.
   */
  destroy() {
    const { controller } = this.children;

    this.accordion.ariaDestroy();
    this.accordion = undefined;
    controller.disabled = true;
  }

  /**
   * Determine if the current media query matches the breakpoint to activate the accordion.
   *
   * @returns bool
   */
  isActiveBreakpoint() {
    const { accordionBreakpoint: breakpoint } = this.element.dataset;
    return (breakpoint === undefined || (breakpoint && breakpoints[breakpoint].matches));
  }

  /**
   * Initialize accordion
   */
  initAccordion() {
    const {
      controller,
      content,
    } = this.children;

    // Do nothing if there is no controller or content.
    if (!controller || !content) {
      return;
    }

    const { accordionLoadOpen: loadOpen = true } = this.element.dataset;

    controller.disabled = false;

    this.accordion = new AriaAccordion({
      controller,
      target: content,
      loadOpen,
    });
  }

  /**
   * Initialize article footnotes/references accordion "slide to" functionality.
   *
   * On research articles, the accordion drawer is used for references and footnotes.
   * This function contains selectors specific to this use case. At somepoint, it
   * might make sense to abstract this further so it can be used elsewhere but
   * it's not necessary at this time.
   */
  initArticleFootnotes() {
    /**
     * We assume that reference/footnote hashes will take the following
     * anchor selector patterns that we're querying for.
     * `#F` = 'footnote' / `#B` or `#R` = 'reference'
     */
    const hashes = document.querySelectorAll('.entry-content a[href^="#bib"], .entry-content a[href^="#R"], .entry-content a[href^="#F"], .entry-content a[href^="#B"]');

    if (!hashes) {
      return; // can't do anything further.
    }

    hashes.forEach((hash) => {
      hash.addEventListener('click', (e) => {

        // Negotiate the hash of the element that was clicked.
        let hashString = '';
        if ( 'undefined' === typeof e.target.hash ) {
          // The target is an element contained within a link. Look for an ancestor link.
          hashString = e.target.closest('a').hash;
        } else {
          hashString = e.target.hash;
        }

        // Dispatch the `slideToRef` event to Accordion instance.
        this.element.dispatchEvent(
          new CustomEvent('slideToRef', {
            detail: { hash: hashString }, // pass along the hash that was clicked.
          }),
        );
      });
    });

    /**
     * Add `slideToRef` custom event to accordion instance to pass along event
     * information on which reference anchor in the article text was clicked on.
     */
    this.element.addEventListener('slideToRef', (e) => {
      const { hash = '' } = e.detail;

      if (!hash) {
        return;
      }

      const hashRef = hash.substring(1);

      // The element with the hash reference we want to slide to.
      const hashToGo = this.element.querySelector(`[name="${hashRef}"], [id="${hashRef}"]`);
      // The accordion instance that contains the hash ref we want to slide to.
      const accordionEl = hashToGo?.closest('*[data-component="accordion"]') ?? null;

      if (accordionEl) {
        // Open accordion drawer
        this.accordion.ariaShow();
        // Important: set a slight delay until after ARIA properties have been updated.
        setTimeout(
          () => hashToGo.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
            inline: 'start',
          }),
          500,
        );
      }
    });
  }
}
